import { DollarSign } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { InfoHint } from "@/components/ui/info-hint";
import { KpiBasisLabel, KpiBasisNote } from "@/components/reports/KpiBasisLabel";
import { useRevenueSnapshot } from "@/hooks/useInvoices";
import { SectionLoadError } from "@/components/invoicing/SectionErrorBoundary";
import { useQueryClient } from "@tanstack/react-query";
import { formatKpiCurrency } from "@/lib/formats/currency";

interface RevenueSnapshotCardProps {
  tramsAccountId?: number;
  viewMode: "my" | "all";
  returnDateFrom?: string;
  returnDateTo?: string;
}

export function RevenueSnapshotCard({
  tramsAccountId,
  viewMode,
  returnDateFrom,
  returnDateTo,
}: RevenueSnapshotCardProps) {
  const queryClient = useQueryClient();
  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;

  const {
    data,
    isLoading,
    error,
  } = useRevenueSnapshot({ tramsAccountId, viewMode, returnDateFrom, returnDateTo });

  if (error) {
    return (
      <SectionLoadError
        sectionName="Revenue Snapshot"
        error={error}
        onRetry={() => {
          queryClient.invalidateQueries({ queryKey: ["/api/invoices/revenue-snapshot"] });
        }}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="rounded-2xl bg-muted/60 p-5 space-y-4">
        <div className="flex items-center gap-2">
          <DollarSign className="h-4 w-4 text-muted-foreground" />
          <Skeleton className="h-4 w-32" />
        </div>
        <div className="rounded-xl bg-card border p-5 space-y-2">
          <div className="flex gap-6">
            <Skeleton className="h-8 w-28" />
            <Skeleton className="h-8 w-28" />
          </div>
          <Skeleton className="h-3 w-48 mx-auto" />
        </div>
        <div className="rounded-xl bg-card border p-5 space-y-2">
          <div className="flex gap-6">
            <Skeleton className="h-8 w-28" />
            <Skeleton className="h-8 w-28" />
          </div>
          <Skeleton className="h-3 w-52 mx-auto" />
        </div>
      </div>
    );
  }

  const avgSale = parseFloat(data?.avgSalePerTrip ?? "0");
  const avgComm = parseFloat(data?.avgCommissionPerTrip ?? "0");
  const tripCount12m = data?.tripCount12m ?? 0;
  const hasAvgData = tripCount12m > 0;

  const projSales = parseFloat(data?.projectedNextYearSales ?? "0");
  const projComm = parseFloat(data?.projectedNextYearCommission ?? "0");
  const projTripCount = data?.projectedNextYearTripCount ?? 0;
  const hasProjectedData = projTripCount > 0;
  const hasReturnDateFilter = Boolean(
    returnDateFrom?.trim() || returnDateTo?.trim(),
  );

  return (
    <div className="rounded-2xl bg-muted/60 p-5 flex flex-col gap-4">
      {/* Title row */}
      <div className="flex items-center gap-1.5">
        <DollarSign className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-sm font-semibold">Revenue Snapshot</h3>
        <InfoHint label="How revenue snapshot metrics are calculated" contentClassName="max-w-[340px]">
          <p>
            {hasReturnDateFilter
              ? "Average sale per trip and average gross commission per trip use trips with at least one closed invoice whose trip return date falls in the selected range, then average those trips over the rolling 12-month payment window."
              : "Average sale per trip and average gross commission per trip use trips with at least one closed invoice paid in the rolling 12-month period ending today."}
          </p>
          <p className="mt-1.5">
            {nextYear} figures are sales and gross commission on the books from
            open invoices returning in {nextYear}. These are based on invoiced
            bookings, not a forecast, and only include open (not paid)
            invoices. Gross is before agency split.
          </p>
        </InfoHint>
      </div>
      <KpiBasisLabel card="revenueSnapshot" />

      {/* Current year averages */}
      <div className="rounded-xl bg-card border px-5 py-4">
        {/* AR-316: stacks below sm — at 375px "AVG. Commission/Trip" and
            "<year> Commission" wrapped while their partners did not. */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-0">
          <div>
            <p className="text-3xl font-bold tracking-tight text-foreground leading-tight">
              {hasAvgData ? formatKpiCurrency(avgSale) : "N/A"}
            </p>
            <p className="text-xs text-muted-foreground mt-1 uppercase tracking-wider">
              AVG. Sale/Trip
            </p>
          </div>
          <div className="min-w-0 sm:border-l sm:pl-5">
            <p className="text-3xl font-bold tracking-tight text-foreground leading-tight">
              {hasAvgData ? formatKpiCurrency(avgComm) : "N/A"}
            </p>
            <p className="text-xs text-muted-foreground mt-1 uppercase tracking-wider">
              AVG. Gross Commission/Trip
            </p>
          </div>
        </div>
        <p className="text-sm text-muted-foreground text-center mt-3 pt-2 border-t border-dashed">
          Calculated over the last 12 months
        </p>
      </div>

      {/* Projected next year */}
      <div className="rounded-xl bg-card border px-5 py-4">
        {/* AR-316: stacks below sm — at 375px "AVG. Commission/Trip" and
            "<year> Commission" wrapped while their partners did not. */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-0">
          <div>
            <p className="text-3xl font-bold tracking-tight text-foreground leading-tight">
              {hasProjectedData ? formatKpiCurrency(projSales) : "N/A"}
            </p>
            <p className="text-xs text-muted-foreground mt-1 uppercase tracking-wider">
              {nextYear} Sales
            </p>
          </div>
          <div className="min-w-0 sm:border-l sm:pl-5">
            <p className="text-3xl font-bold tracking-tight text-foreground leading-tight">
              {hasProjectedData ? formatKpiCurrency(projComm) : "N/A"}
            </p>
            <p className="text-xs text-muted-foreground mt-1 uppercase tracking-wider">
              {nextYear} Gross Commission
            </p>
          </div>
        </div>
        <p className="text-sm text-muted-foreground text-center mt-3 pt-2 border-t border-dashed">
          {hasProjectedData
            ? "Estimate \u2014 based on open invoices returning in " + nextYear
            : "No open invoices with return dates in " + nextYear}
        </p>
      </div>
    </div>
  );
}
